import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link
} from '@mui/material';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RepeatIcon from '@mui/icons-material/Repeat';
import TimerIcon from '@mui/icons-material/Timer';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const AlertSummary = ({ alertData }) => {
  const { 
    name, 
    type, 
    triggerType,
    mainFilters = [],
    conditions = {},
    channels = {}
  } = alertData;
  
  // Get alert type info
  const getAlertTypeInfo = () => {
    switch (type) {
      case 'priceAlert':
        return {
          name: 'Price Alert',
          icon: <TrendingUpIcon />,
          color: '#4CAF50' // green
        };
      case 'congressTrade':
        return {
          name: 'Congressional Trading Alert',
          icon: <PersonIcon />,
          color: '#9C27B0' // purple
        };
      case 'newsAlert':
        return {
          name: 'News Alert',
          icon: <ArticleIcon />,
          color: '#2196F3' // blue
        };
      case 'insiderTrade':
        return {
          name: 'Insider Trading Alert',
          icon: <BusinessIcon />,
          color: '#FF9800' // orange
        };
      case 'marketEvent':
        return {
          name: 'Market Event Alert',
          icon: <EventIcon />,
          color: '#F44336' // red
        };
      default:
        return {
          name: 'Alert',
          icon: <NotificationsIcon />,
          color: '#757575' // gray
        };
    }
  };
  
  // Get configured channels
  const getEnabledChannels = () => {
    const enabledChannels = [];
    
    if (channels.site) {
      enabledChannels.push({
        name: 'Site Notifications',
        icon: <NotificationsIcon />,
        color: '#2196F3' // blue
      });
    }
    
    if (channels.email) {
      enabledChannels.push({
        name: 'Email',
        icon: <EmailIcon />,
        color: '#4CAF50' // green
      });
    }
    
    if (channels.telegram) {
      enabledChannels.push({
        name: 'Telegram',
        icon: <TelegramIcon />,
        color: '#2CA5E0' // telegram blue
      });
    }
    
    if (channels.discord) {
      enabledChannels.push({
        name: 'Discord',
        icon: <ChatIcon />,
        color: '#7289DA' // discord purple
      });
    }
    
    if (channels.whatsapp) {
      enabledChannels.push({
        name: 'WhatsApp',
        icon: <WhatsAppIcon />,
        color: '#25D366' // whatsapp green
      });
    }
    
    return enabledChannels;
  };
  
  // Get alert type specific summary
  const getAlertTypeSummary = () => {
    switch (type) {
      case 'priceAlert':
        return getPriceAlertSummary();
      case 'congressTrade':
        return getCongressionalTradeSummary();
      case 'newsAlert':
        return getNewsAlertSummary();
      case 'insiderTrade':
        return getInsiderTradeSummary();
      case 'marketEvent':
        return getMarketEventSummary();
      default:
        return null;
    }
  };
  
  // Price alert summary
  const getPriceAlertSummary = () => {
    const symbols = mainFilters
      .filter(filter => filter.type === 'symbol')
      .map(filter => filter.value);
    
    const directionText = {
      'above': 'rises above',
      'below': 'falls below',
      'up': 'increases by',
      'down': 'decreases by'
    };
    
    let conditionDescription = '';
    if (conditions.direction === 'above' || conditions.direction === 'below') {
      conditionDescription = `${directionText[conditions.direction]} $${conditions.price}`;
      if (conditions.crossingOnly) {
        conditionDescription += ' (crossing only)';
      }
    } else if (conditions.direction === 'up' || conditions.direction === 'down') {
      conditionDescription = `${directionText[conditions.direction]} ${conditions.threshold}%`;
    }
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          This alert will notify you when:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemIcon>
              <ShowChartIcon />
            </ListItemIcon>
            <ListItemText
              primary={conditions.symbol || (symbols.length > 0 ? symbols[0] : 'Any stock')}
              secondary={symbols.length > 1 ? `and ${symbols.length - 1} other stocks` : ''}
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <TrendingUpIcon />
            </ListItemIcon>
            <ListItemText
              primary={conditionDescription}
              secondary={`Alert will ${triggerType === 'once' ? 'trigger once' : 'trigger continuously'}`}
            />
          </ListItem>
        </List>
      </Box>
    );
  };
  
  // Congressional trade summary
  const getCongressionalTradeSummary = () => {
    const representatives = mainFilters
      .filter(filter => filter.type === 'representative')
      .map(filter => filter.value);
    
    const symbols = mainFilters
      .filter(filter => filter.type === 'symbol')
      .map(filter => filter.value);
    
    const minAmount = conditions.minAmount 
      ? `$${Number(conditions.minAmount).toLocaleString()}` 
      : 'any amount';
    
    const tradeType = conditions.tradeType || 'any trade type';
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          This alert will notify you when:
        </Typography>
        
        <List>
          {representatives.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary={representatives.length === 1 ? representatives[0] : `${representatives.length} Representatives`}
                secondary={representatives.length > 1 ? `Including ${representatives.slice(0, 3).join(', ')}${representatives.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          {symbols.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <ShowChartIcon />
              </ListItemIcon>
              <ListItemText
                primary={symbols.length === 1 ? symbols[0] : `${symbols.length} Stocks`}
                secondary={symbols.length > 1 ? `Including ${symbols.slice(0, 3).join(', ')}${symbols.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          <ListItem>
            <ListItemIcon>
              <AttachMoneyIcon />
            </ListItemIcon>
            <ListItemText
              primary={`Transactions of ${minAmount} or more`}
              secondary={`Trade type: ${tradeType}`}
            />
          </ListItem>
        </List>
      </Box>
    );
  };
  
  // News alert summary
  const getNewsAlertSummary = () => {
    const symbols = mainFilters
      .filter(filter => filter.type === 'symbol')
      .map(filter => filter.value);
    
    const keywords = mainFilters
      .filter(filter => filter.type === 'keyword')
      .map(filter => filter.value);
    
    const sentimentInfo = conditions.sentiment 
      ? `${conditions.sentiment} sentiment` 
      : 'any sentiment';
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          This alert will notify you when:
        </Typography>
        
        <List>
          {symbols.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <ShowChartIcon />
              </ListItemIcon>
              <ListItemText
                primary={symbols.length === 1 ? symbols[0] : `${symbols.length} Stocks`}
                secondary={symbols.length > 1 ? `Including ${symbols.slice(0, 3).join(', ')}${symbols.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          {keywords.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <KeyboardIcon />
              </ListItemIcon>
              <ListItemText
                primary={keywords.length === 1 ? `"${keywords[0]}"` : `${keywords.length} Keywords`}
                secondary={keywords.length > 1 ? `Including "${keywords.slice(0, 3).join('", "')}"${keywords.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          <ListItem>
            <ListItemIcon>
              <ArticleIcon />
            </ListItemIcon>
            <ListItemText
              primary={`News with ${sentimentInfo}`}
              secondary={conditions.sentimentComparison ? `Sentiment score ${conditions.sentimentComparison === 'greater' ? 'greater than' : 'less than'} ${conditions.sentimentScore}` : ''}
            />
          </ListItem>
        </List>
      </Box>
    );
  };
  
  // Insider trade summary
  const getInsiderTradeSummary = () => {
    const symbols = mainFilters
      .filter(filter => filter.type === 'symbol')
      .map(filter => filter.value);
    
    const positions = conditions.positions || [];
    const minAmount = conditions.minAmount 
      ? `$${Number(conditions.minAmount).toLocaleString()}` 
      : 'any amount';
    
    const minShares = conditions.minShares
      ? `${Number(conditions.minShares).toLocaleString()} shares`
      : 'any number of shares';
    
    const transactionType = conditions.transactionType || 'any transaction type';
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          This alert will notify you when:
        </Typography>
        
        <List>
          {symbols.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <ShowChartIcon />
              </ListItemIcon>
              <ListItemText
                primary={symbols.length === 1 ? symbols[0] : `${symbols.length} Stocks`}
                secondary={symbols.length > 1 ? `Including ${symbols.slice(0, 3).join(', ')}${symbols.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          {positions.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText
                primary={positions.length === 1 ? positions[0] : `${positions.length} Executive Positions`}
                secondary={positions.length > 1 ? `Including ${positions.slice(0, 3).join(', ')}${positions.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          <ListItem>
            <ListItemIcon>
              <AttachMoneyIcon />
            </ListItemIcon>
            <ListItemText
              primary={`Transactions of ${minAmount} or ${minShares}`}
              secondary={`Transaction type: ${transactionType}`}
            />
          </ListItem>
        </List>
      </Box>
    );
  };
  
  // Market event summary
  const getMarketEventSummary = () => {
    const symbols = mainFilters
      .filter(filter => filter.type === 'symbol')
      .map(filter => filter.value);
    
    const eventTypes = conditions.eventTypes || [];
    
    // Check if this is a daily earnings alert
    const isDailyEarnings = eventTypes.includes('earnings');
    
    return (
      <Box>
        <Typography variant="body1" paragraph>
          This alert will notify you when:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                isDailyEarnings 
                  ? 'Daily Earnings Announcements' 
                  : eventTypes.length === 1 
                    ? getEventTypeName(eventTypes[0])
                    : `${eventTypes.length} Event Types`
              }
              secondary={
                !isDailyEarnings && eventTypes.length > 1 
                  ? `Including ${eventTypes.slice(0, 3).map(getEventTypeName).join(', ')}${eventTypes.length > 3 ? '...' : ''}`
                  : ''
              }
            />
          </ListItem>
          
          {!isDailyEarnings && symbols.length > 0 && (
            <ListItem>
              <ListItemIcon>
                <ShowChartIcon />
              </ListItemIcon>
              <ListItemText
                primary={symbols.length === 1 ? symbols[0] : `${symbols.length} Stocks`}
                secondary={symbols.length > 1 ? `Including ${symbols.slice(0, 3).join(', ')}${symbols.length > 3 ? '...' : ''}` : ''}
              />
            </ListItem>
          )}
          
          {isDailyEarnings && (
            <ListItem>
              <ListItemIcon>
                <AttachMoneyIcon />
              </ListItemIcon>
              <ListItemText
                primary="Daily summary of companies reporting earnings"
                secondary="No additional configuration needed"
              />
            </ListItem>
          )}
        </List>
      </Box>
    );
  };
  
  // Helper to get event type name
  const getEventTypeName = (eventType) => {
    switch (eventType) {
      case 'earnings':
        return 'Earnings Announcements';
      case 'dividends':
        return 'Dividend Announcements';
      case 'splits':
        return 'Stock Splits';
      case 'ipo':
        return 'IPO Announcements';
      case 'economic':
        return 'Economic Data Releases';
      default:
        return eventType;
    }
  };
  
  const alertTypeInfo = getAlertTypeInfo();
  const enabledChannels = getEnabledChannels();
  
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Review Alert
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Review your alert details before creating it.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box 
                sx={{ 
                  color: alertTypeInfo.color,
                  mr: 1
                }}
              >
                {alertTypeInfo.icon}
              </Box>
              <Typography variant="h6">
                {alertTypeInfo.name}
              </Typography>
            </Box>
            
            <Typography variant="h5" gutterBottom>
              {name}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              {triggerType === 'once' ? (
                <Chip 
                  icon={<TimerIcon />} 
                  label="One-time alert" 
                  variant="outlined" 
                  size="small" 
                  sx={{ mr: 1 }}
                />
              ) : (
                <Chip 
                  icon={<RepeatIcon />} 
                  label="Continuous alert" 
                  variant="outlined" 
                  size="small"
                  sx={{ mr: 1 }}
                />
              )}
              
              <Typography variant="body2" color="text.secondary">
                {triggerType === 'once' 
                  ? 'Alert will trigger once and then deactivate'
                  : 'Alert will trigger continuously when conditions are met'}
              </Typography>
            </Box>
            
            {getAlertTypeSummary()}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Delivery Channels
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            {enabledChannels.length === 0 ? (
              <Typography variant="body2" color="error">
                No delivery channels selected. Please select at least one channel.
              </Typography>
            ) : (
              <List>
                {enabledChannels.map((channel, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ color: channel.color }}>
                      {channel.icon}
                    </ListItemIcon>
                    <ListItemText primary={channel.name} />
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItem>
                ))}
              </List>
            )}
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                You can manage your notification channels in your account settings.
              </Typography>
              
              <Link 
                href="/account/channels" 
                underline="hover"
                sx={{ display: 'block', mt: 1 }}
              >
                Manage notification channels
              </Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Ready to create this alert? Click "Create Alert" to continue.
        </Typography>
      </Box>
    </Box>
  );
};

export default AlertSummary;